import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';

const BlogPage = () => {
  const { language } = useLanguage();
  
  return (
    <main id="main-content" role="main" className="pt-32" data-testid="blog-page">
      <SEOHead
        title="Blog Fitness Nyon | Conseils Personal Training, EMS, Nutrition | Body-Up"
        description="Conseils fitness & personal training Nyon : perte de poids, musculation, EMS Drysuit, nutrition sportive. Articles d'experts Body-Up sur La Côte vaudoise."
        path="/blog"
      />
      {/* SEO Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackNav />
          <AnimatedSection>
            <div className="text-center mb-16">
              <SectionBadge emoji="📚">Blog Fitness Nyon</SectionBadge>
              <h1 className="text-4xl md:text-6xl font-bold text-white mt-4">
                {language === 'fr' ? "Conseils " : "Expert "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-pink-400">
                  {language === 'fr' ? "d'Experts" : "Tips"}
                </span>
              </h1>
              <p className="text-gray-400 mt-6 max-w-3xl mx-auto text-lg">
                {language === 'fr' 
                  ? "Personal training, EMS, musculation, perte de poids : découvrez nos articles pour optimiser vos résultats à Nyon et sur la Côte."
                  : "Personal training, EMS, weight training, weight loss: discover our articles to optimize your results in Nyon and the La Côte region."
                }
              </p>
              {/* SEO Keywords */}
              <div className="flex flex-wrap justify-center gap-2 mt-8">
                {['Coach Nyon', 'EMS Training', 'Musculation', 'Perte de poids', 'Studio privé'].map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-white/10 text-gray-300 text-sm rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </AnimatedSection>

          {/* All Articles Grid - No "See More" button needed */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogArticles.map((article, index) => (
              <AnimatedSection key={article.id} delay={index * 100}>
                <Link to={`/blog/${article.id}`} className="block h-full">
                  <article className="group bg-gradient-to-br from-white/10 to-white/5 rounded-2xl overflow-hidden border border-white/20 hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/20 h-full flex flex-col">
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={article.image}
                        alt={language === 'fr' ? (article.imageAlt || article.title) : (article.imageAltEn || article.titleEn)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                      <div className="absolute top-4 left-4">
                        <span className="px-3 py-1 bg-primary/90 text-white text-xs font-medium rounded-full">
                          {language === 'fr' ? article.category : article.categoryEn}
                        </span>
                      </div>
                      <div className="absolute bottom-4 left-4">
                        <span className="text-4xl" role="img" aria-label={article.category}>{article.emoji}</span>
                      </div>
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                        <Clock className="w-4 h-4" aria-hidden="true" />
                        <span>{article.readTime} {language === 'fr' ? 'de lecture' : 'read'}</span>
                      </div>
                      <h2 className="text-xl font-bold text-white mb-3 group-hover:text-primary transition-colors">
                        {language === 'fr' ? article.title : article.titleEn}
                      </h2>
                      <p className="text-gray-400 text-sm flex-1">
                        {language === 'fr' ? article.excerpt : article.excerptEn}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all">
                        {language === 'fr' ? 'Lire l\'article' : 'Read article'}
                        <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </div>
                    </div>
                  </article>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          {/* CTA Section */}
          <AnimatedSection delay={600}>
            <div className="mt-16 text-center">
              <div className="inline-block relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary via-pink-500 to-primary rounded-3xl blur-2xl opacity-20" />
                <div className="relative bg-gradient-to-r from-primary/20 via-pink-500/20 to-primary/20 backdrop-blur-md rounded-2xl p-8 border border-primary/30">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {language === 'fr' ? "🎁 Essayez gratuitement" : "🎁 Try for free"}
                  </h3>
                  <p className="text-gray-300 mb-6">
                    {language === 'fr' 
                      ? "Consultation gratuite et sans engagement à Nyon"
                      : "Free consultation with no commitment in Nyon"
                    }
                  </p>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30">
                      <Calendar className="w-5 h-5 mr-2" aria-hidden="true" />
                      {language === 'fr' ? "Réserver ma consultation gratuite" : "Book my free consultation"}
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Blog Article Page - SEO Optimized

export default BlogPage;
